import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, map, of, tap } from 'rxjs';
import { toIdentifiableEntities } from 'src/app/helpers/graphql';
import {
  Monitor,
  MonitorSpec,
  resolveProbeSettings,
  resolveRegionSettings,
} from 'src/app/models/monitor';
import { ProberRegion } from 'src/app/models/prober-region';
import { ToastService } from 'src/app/services/toast/toast.service';
import {
  PaginatedListEntities,
  PaginatedListEntitiesState,
} from 'src/app/store/common/paginated-list-entity';
import {
  OffsetPagination,
  OffsetPaginationState,
} from 'src/app/store/common/pagination';
import { UserState } from 'src/app/store/user/user.state';
import {
  addListEntity,
  ListEntities,
  loadEntities,
  NewListEntities,
  updateListEntity,
} from '../common/list-entity';
import { ServicesService } from '../services/services.service';
import {
  CreateMonitorAction,
  LoadMonitorsAction,
  LoadMonitorsForServiceAction,
  LoadProberRegionsAction,
  MonitorUpdatedAction,
  UpdateMonitorAction,
} from './monitors.actions';
import { MonitorService } from './monitors.service';

export interface MonitorStateModel {
  monitors: PaginatedListEntitiesState<Monitor, OffsetPaginationState>;
  proberRegions: ListEntities<ProberRegion>;
}

@State<MonitorStateModel>({
  name: 'monitors',
  defaults: {
    monitors: OffsetPagination.GetDefaultState(),
    proberRegions: NewListEntities<ProberRegion>(),
  },
  children: [],
})
@Injectable()
export class MonitorState {
  store = inject(Store);
  toast = inject(ToastService);
  monitorService = inject(MonitorService);
  servicesService = inject(ServicesService);

  monitorPagination = new PaginatedListEntities<
    MonitorStateModel,
    OffsetPagination
  >('monitors', new OffsetPagination());

  @Selector()
  static monitors(state: MonitorStateModel) {
    return state.monitors;
  }

  @Selector()
  static proberRegions(state: MonitorStateModel) {
    return state.proberRegions;
  }

  @Action(LoadProberRegionsAction)
  loadProberRegions(ctx: StateContext<MonitorStateModel>) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );

    if (!organizationID) {
      throw new Error('No organization ID set');
    }

    return loadEntities(
      ctx,
      'proberRegions',
      this.monitorService
        .loadProberRegions(organizationID)
        .pipe(map(regions => toIdentifiableEntities(regions, 'slug')))
    );
  }

  @Action(LoadMonitorsAction)
  loadMonitors(
    ctx: StateContext<MonitorStateModel>,
    { options, refresh }: LoadMonitorsAction
  ) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );

    if (!organizationID) {
      throw new Error('No organization ID set');
    }

    return this.monitorPagination.loadEntititesPaginated(
      ctx,
      options.pagination,
      this.monitorService.loadMonitors(organizationID, options),
      false,
      refresh
    );
  }

  @Action(LoadMonitorsForServiceAction)
  loadMonitorsForService(
    ctx: StateContext<MonitorStateModel>,
    { serviceId, options }: LoadMonitorsForServiceAction
  ) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );

    if (!organizationID) {
      throw new Error('No organization ID set');
    }

    return this.monitorPagination.loadEntititesPaginated(
      ctx,
      options.pagination,
      this.servicesService.loadMonitorsForService(
        organizationID,
        serviceId,
        options
      )
    );
  }

  @Action(CreateMonitorAction)
  createMonitor(
    ctx: StateContext<MonitorStateModel>,
    { spec }: CreateMonitorAction
  ) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );

    if (!organizationID) {
      throw new Error('No organization ID set');
    }

    return addListEntity<MonitorStateModel, Monitor>(
      ctx,
      'monitors',
      'id',
      this.monitorService.createMonitor(organizationID, spec)
    ).pipe(
      catchError(err => {
        this.toast.showError(err);
        return of();
      })
    );
  }

  @Action(UpdateMonitorAction)
  updateMonitor(
    ctx: StateContext<MonitorStateModel>,
    { monitor, spec }: UpdateMonitorAction
  ) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );
    if (!organizationID) {
      throw new Error('No organizationID set');
    }

    const probe = resolveProbeSettings(monitor, spec);
    const region = resolveRegionSettings(monitor, spec);

    const fullSpec: MonitorSpec = {
      name: spec.name ?? monitor.name,
      publicServiceId: spec.publicServiceId ?? monitor.service!.id,
      probeType: probe.probeType,
      scheduling: {
        intervalSeconds:
          spec.scheduling?.intervalSeconds ??
          monitor.scheduling.intervalSeconds,
        retries: spec.scheduling?.retries ?? monitor.scheduling.retries,
      },
      paused: spec.paused ?? monitor.paused,
      icmpProbeConfiguration: probe.icmpProbeConfiguration,
      httpProbeConfiguration: probe.httpProbeConfiguration,
      gameProbeConfiguration: probe.gameProbeConfiguration,
      proberRegionScopes: region.proberRegionScopes,
      availabilityPolicy: region.availabilityPolicy,
    };

    return updateListEntity(
      ctx,
      'monitors',
      monitor.id,
      this.monitorService.updateMonitor(organizationID, monitor.id, fullSpec),
      true
    ).pipe(
      tap((updated: Monitor) =>
        this.store.dispatch(new MonitorUpdatedAction(updated))
      ),
      catchError((err: any) => {
        this.toast.showError(err);
        throw err;
      })
    );
  }
}
